import { ChangeDetectionStrategy, Component, computed, ElementRef, input, output, signal } from '@angular/core';
import { MessageEx } from '../../../classes/messageex';
import { ChatLoggingService } from '../../../services/chat-logging.service';
import { ScrollingService } from '../../../services/scrolling.service';
import { ContentTypes } from '../../../classes/constants';
import { PrimeThemeService } from '../../../themes/prime-theme.service';

@Component({
  selector: 'app-attachment-card',
  templateUrl: './attachment-card.component.html',
  styleUrls: ['./attachment-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttachmentCardComponent {
  // Signal-based inputs
  isOfflineCache = input<boolean>();
  item = input<MessageEx>();
  attachmentName = input<string>();
  attachmentSize = input<string>();
  currentTabIndex = input<string>();
  blobUrl = input<string>();
  isOffline = input<boolean>();
  isMobile = input<boolean>();
  clickToLoadImage = input<boolean>();
  isMine = input<boolean>();
  imageViewerUrl = input<any>();
  isErrorMessage = input<boolean>();
  contentType = input<string>();
  simpleType = input<string>();
  previewMode = input<boolean>(false);
  chatItemElement = input<ElementRef>();

  // Computed signal to detect HTML anchor content in attachmentName
  hasHtmlAnchorContent = computed(() => {
    const name = this.attachmentName();
    return name ? /<a\s+[^>]*href/i.test(name) : false;
  });

  // Signal-based outputs
  isErrorMessageChange = output<boolean>();
  contentTypeChange = output<string>();
  onFullScreenImage = output<void>();
  onDownloadAttachment = output<string>();
  onLoadImageAttachment = output<void>();
  onCloseOptions = output<any>();

  // Convert to signals for internal state management
  public clickToLoadImageExpanded = signal(false);
  public clickToLoadImageLoaded = signal(false);

  constructor(
    private chatLoggingService: ChatLoggingService,
    private scrollingService: ScrollingService,
    public primeThemeService: PrimeThemeService
  ) {}

  fullScreenImage() {
    if (!this.previewMode()) {
      this.onFullScreenImage.emit();
    }
  }

  downloadAttachment() {
    if (!this.previewMode()) {
      this.onDownloadAttachment.emit(this.item()?.clCompChat_displayBody);
    }
  }

  // Private property for contentType updates since it's both input and needs to be modified
  private _contentType: string;

  public async expandImage($event) {
    try {
      if (!this.clickToLoadImageLoaded()) {
        this.clickToLoadImageLoaded.set(true);
        if (this.scrollingService.isNearBottom()) {
          this.scrollingService.scrollToBottom();
        }
        this.onLoadImageAttachment.emit();
      }
      this.clickToLoadImageExpanded.set(!this.clickToLoadImageExpanded());
      this.onCloseOptions.emit($event);
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'attachment-card.component.expandImage(): Error loading image',
        logParameters: [this.item()?.messageId, ex]
      });
      this.isErrorMessageChange.emit(true);
      this._contentType = ContentTypes.ERROR;
      this.contentTypeChange.emit(this._contentType);
    }
  }
}
